import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, ChevronLeft, Bug, Lightbulb, Heart, MessageCircle, BadgeCheck, Send, CheckCircle2, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { timeAgo } from "@/lib/format";

const ICON = { bug: Bug, idea: Lightbulb, praise: Heart, other: MessageCircle };
const ACCENT = { bug: "text-destructive", idea: "text-bloq-blue", praise: "text-bloq-mint", other: "text-bloq-muted" };

function StatusBadge({ f }) {
  if (f.confirmed_at)
    return (
      <span className="flex items-center gap-1 rounded-full bg-bloq-mint/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-bloq-mint" data-testid="my-feedback-badge-confirmed">
        <BadgeCheck className="h-2.5 w-2.5" /> Confirmed
      </span>
    );
  if (f.status === "resolved")
    return (
      <span className="rounded-full bg-bloq-mint/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-bloq-mint" data-testid="my-feedback-badge-resolved">
        Resolved
      </span>
    );
  return (
    <span className="rounded-full bg-amber-400/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-400" data-testid="my-feedback-badge-open">
      Open
    </span>
  );
}

// Reporter-side beta feedback loop (§11.3): view my reports, reply to the team, confirm a fix.
export function MyBetaFeedback({ active, onConfirmed }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.myBetaFeedback();
      setItems(res?.feedback || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load your feedback.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active && items === null) load();
  }, [active, items, load]);

  const selected = items?.find((f) => f.id === selectedId) || null;

  const sendReply = async () => {
    const body = replyText.trim();
    if (!body || !selected) return;
    setBusy(true);
    try {
      const res = await api.replyBetaFeedback(selected.id, body);
      setItems((cur) => cur.map((f) => (f.id === selected.id ? { ...f, replies: [...(f.replies || []), res.reply] } : f)));
      setReplyText("");
      toast.success("Reply sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reply.");
    } finally {
      setBusy(false);
    }
  };

  const confirmFix = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await api.confirmBetaFeedback(selected.id);
      setItems((cur) => cur.map((f) => (f.id === selected.id ? { ...f, confirmed_at: res.confirmed_at } : f)));
      toast.success("Thanks — fix confirmed 🎉");
      onConfirmed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't confirm the fix.");
    } finally {
      setBusy(false);
    }
  };

  if (loading && items === null) {
    return (
      <div className="mt-4 flex items-center justify-center py-10 text-bloq-muted" data-testid="my-feedback-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (selected) {
    const Icon = ICON[selected.category] || MessageCircle;
    const resolved = selected.status === "resolved";
    return (
      <div className="mt-3" data-testid="my-feedback-detail">
        <button onClick={() => setSelectedId(null)} className="mb-2 flex items-center gap-1 text-xs text-bloq-muted transition-colors hover:text-white" data-testid="my-feedback-back">
          <ChevronLeft className="h-4 w-4" /> Back to my feedback
        </button>
        <div className="max-h-[44vh] space-y-2.5 overflow-y-auto pr-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-bloq-muted">
            <span className={cn("shrink-0", ACCENT[selected.category])}><Icon className="h-4 w-4" /></span>
            <span className="font-bold uppercase tracking-wide">{selected.category}</span>
            <span>· {timeAgo(selected.created_at)}</span>
            <StatusBadge f={selected} />
          </div>
          <div className="rounded-xl border border-white/10 bg-bloq-bg/40 px-3 py-2 text-sm text-white/90" data-testid="my-feedback-message">
            {selected.message}
          </div>
          {resolved && (
            <div className="rounded-xl border border-bloq-mint/20 bg-bloq-mint/5 px-3 py-2" data-testid="my-feedback-resolution">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-bloq-mint">
                <CheckCircle2 className="h-3.5 w-3.5" /> Marked resolved{selected.resolved_at ? ` · ${timeAgo(selected.resolved_at)}` : ""}
              </div>
              {selected.resolution_note && <p className="mt-1 text-xs text-white/80">{selected.resolution_note}</p>}
            </div>
          )}
          {[
            ...(selected.replies || []).map((r) => ({ ...r, _from: "you" })),
            ...(selected.team_replies || []).map((r) => ({ ...r, _from: "team" })),
          ]
            .sort((a, b) => new Date(a.at) - new Date(b.at))
            .map((r) =>
              r._from === "team" ? (
                <div key={`t-${r.id}`} className="mr-6 rounded-xl border border-bloq-mint/25 bg-bloq-mint/10 px-3 py-2" data-testid={`my-feedback-teamreply-${r.id}`}>
                  <div className="text-sm text-white/90">{r.body}</div>
                  <div className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-bloq-mint">
                    <BadgeCheck className="h-3 w-3" /> BLOQ Team · {timeAgo(r.at)}
                  </div>
                </div>
              ) : (
                <div key={`y-${r.id}`} className="ml-6 rounded-xl border border-bloq-blue/20 bg-bloq-blue/10 px-3 py-2" data-testid={`my-feedback-reply-${r.id}`}>
                  <div className="text-sm text-white/90">{r.body}</div>
                  <div className="mt-0.5 text-[10px] text-bloq-muted">You · {timeAgo(r.at)}</div>
                </div>
              ),
            )}
        </div>

        {resolved && !selected.confirmed_at && (
          <button
            onClick={confirmFix}
            disabled={busy}
            className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-full border border-bloq-mint/50 bg-bloq-mint/10 text-sm font-bold text-bloq-mint transition-colors hover:bg-bloq-mint/20 disabled:opacity-50"
            data-testid="my-feedback-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BadgeCheck className="h-4 w-4" /> Confirm the fix works</>}
          </button>
        )}
        {selected.confirmed_at && (
          <div className="mt-3 flex items-center justify-center gap-1.5 rounded-full bg-bloq-mint/15 py-2 text-xs font-semibold text-bloq-mint" data-testid="my-feedback-confirmed-banner">
            <BadgeCheck className="h-4 w-4" /> You confirmed this fix
          </div>
        )}

        <div className="mt-2 flex items-end gap-2">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            rows={1}
            maxLength={2000}
            placeholder="Reply to the team…"
            className="flex-1 resize-none rounded-xl border border-white/12 bg-bloq-bg px-3 py-2 text-sm text-white placeholder:text-bloq-muted focus:outline-none focus:ring-1 focus:ring-bloq-mint"
            data-testid="my-feedback-reply-input"
          />
          <button
            onClick={sendReply}
            disabled={busy || !replyText.trim()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bloq-gradient text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-50"
            data-testid="my-feedback-reply-send"
            aria-label="Send reply"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid="my-feedback-list">
      {items && items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center text-bloq-muted" data-testid="my-feedback-empty">
          <Inbox className="h-6 w-6" />
          <p className="text-xs">
            No feedback yet. Switch to <span className="text-bloq-mint">New</span> to send your first report.
          </p>
        </div>
      ) : (
        <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1">
          {(items || []).map((f) => {
            const Icon = ICON[f.category] || MessageCircle;
            return (
              <button
                key={f.id}
                onClick={() => {
                  setSelectedId(f.id);
                  setReplyText("");
                }}
                className="flex w-full items-start gap-2.5 rounded-xl border border-white/8 bg-bloq-s2 px-3 py-2.5 text-left transition-colors hover:border-bloq-mint/30"
                data-testid={`my-feedback-item-${f.id}`}
              >
                <span className={cn("mt-0.5 shrink-0", ACCENT[f.category])}><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-[10px] text-bloq-muted">
                    <span className="font-bold uppercase tracking-wide">{f.category}</span>
                    <span>· {timeAgo(f.created_at)}</span>
                    <StatusBadge f={f} />
                    {(f.team_replies || []).length > 0 && (
                      <span className="flex items-center gap-0.5 rounded-full bg-bloq-mint/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-bloq-mint" data-testid={`my-feedback-item-teamreplied-${f.id}`}>
                        <BadgeCheck className="h-2.5 w-2.5" /> Team replied
                      </span>
                    )}
                    {(() => {
                      const total = (f.replies || []).length + (f.team_replies || []).length;
                      return total > 0 ? <span data-testid={`my-feedback-item-replies-${f.id}`}>· {total} repl{total === 1 ? "y" : "ies"}</span> : null;
                    })()}
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-xs text-white/85">{f.message}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
